// PayrollPage — read-only view over the "actual" layer (time_entries) for payroll.
// Browser-only CSV export (no backend) plus an open-days view so nothing silently
// drops from a pay period. Hours are always derived via workedHours (instant diff) —
// null for an open day. Notes combine the entry's own note with that day's job summary.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { supabase } from './supabaseClient.js';
import { workedHours, formatInOriginZone } from './timeEntryHours.js';

const todayISO = () => DateTime.now().setZone('America/Vancouver').toISODate();
const daysAgoISO = (n) => DateTime.now().setZone('America/Vancouver').minus({ days: n }).toISODate();

const csvEscape = (v) => `"${(v == null ? '' : String(v)).replace(/"/g, '""')}"`;

const friendly = (e) => {
  const msg = e?.message || String(e);
  if (/time_entr/i.test(msg) || e?.code === '42P01') {
    return 'Time tracking isn’t set up yet — apply migration 013 in Supabase, then reload.';
  }
  return msg;
};

// Only Grant's hours live in this system (Steve & Dallon use a separate one).
// Keyed off the applicator id (stable across renames).
const HOURS_EMPLOYEE_ID = '5cd63d77-cc02-4638-9fe6-39cf2b6d2c69';

const TABS = [
  { key: 'entries', label: 'Entries' },
  { key: 'open', label: 'Open Days' },
];

const PayrollPage = () => {
  const [applicators, setApplicators] = useState([]);
  const [employeeId, setEmployeeId] = useState(null); // locked to Grant (set once applicators load)
  const [startDate, setStartDate] = useState(daysAgoISO(13));
  const [endDate, setEndDate] = useState(todayISO());

  const [tab, setTab] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [openDays, setOpenDays] = useState([]);
  const [routeStarts, setRouteStarts] = useState({});       // route_id → start_date
  const [jobsByRouteDay, setJobsByRouteDay] = useState({}); // `${route_id}|${day}` → [company]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const empName = useCallback(
    (id) => applicators.find((a) => a.id === id)?.name || 'Unknown',
    [applicators]
  );

  // Jobs completed on an entry's day (maps work_date back to the route's day number).
  const jobsForEntry = useCallback((e) => {
    const start = e.route_id ? routeStarts[e.route_id] : null;
    if (!start || !e.work_date) return [];
    const dn = Math.round(DateTime.fromISO(e.work_date).diff(DateTime.fromISO(start), 'days').days) + 1;
    return jobsByRouteDay[`${e.route_id}|${dn}`] || [];
  }, [routeStarts, jobsByRouteDay]);

  // Notes column = the entry's own note plus a summary of that day's jobs.
  const combinedNotes = useCallback((e) => {
    const parts = [];
    if (e.notes && e.notes.trim()) parts.push(e.notes.trim());
    const jobs = jobsForEntry(e);
    if (jobs.length) parts.push(`Jobs: ${jobs.join(', ')}`);
    return parts.join(' — ');
  }, [jobsForEntry]);

  // One-time meta — lock the view to Grant (by id) and load applicators for labels.
  useEffect(() => {
    setEmployeeId(HOURS_EMPLOYEE_ID);
    (async () => {
      const { data } = await supabase.from('applicators').select('id, name').order('name');
      if (data) setApplicators(data);
    })();
  }, []);

  const load = useCallback(async () => {
    if (!employeeId) { setEntries([]); setOpenDays([]); setRouteStarts({}); setJobsByRouteDay({}); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      // Entries in range for the tracked employee
      const { data: entryData, error: entryErr } = await supabase
        .from('time_entries')
        .select('*')
        .eq('employee_id', employeeId)
        .gte('work_date', startDate)
        .lte('work_date', endDate)
        .order('work_date', { ascending: true });
      if (entryErr) throw entryErr;
      setEntries(entryData || []);

      // Open days = started (clock_in set) but not stopped — ANY date, so stragglers surface.
      // Notes-only rows (no clock_in) are intentionally excluded.
      const { data: openData } = await supabase
        .from('time_entries')
        .select('*')
        .eq('employee_id', employeeId)
        .not('clock_in_at', 'is', null)
        .is('clock_out_at', null)
        .order('work_date', { ascending: true });
      setOpenDays(openData || []);

      // Job summaries for the Notes column: each entry's (route, work_date) → that day's jobs.
      const routeIds = [...new Set((entryData || []).map((x) => x.route_id).filter(Boolean))];
      const starts = {};
      const jobs = {};
      if (routeIds.length) {
        const [routesRes, jobItemsRes] = await Promise.all([
          supabase.from('routes').select('id, start_date').in('id', routeIds),
          supabase
            .from('route_items')
            .select('route_id, day_number, crm_job:crm_jobs(company)')
            .in('route_id', routeIds)
            .eq('row_type', 'JOB'),
        ]);
        for (const r of routesRes.data || []) starts[r.id] = r.start_date;
        for (const it of jobItemsRes.data || []) {
          const key = `${it.route_id}|${it.day_number}`;
          (jobs[key] = jobs[key] || []).push(it.crm_job?.company || 'Job');
        }
      }
      setRouteStarts(starts);
      setJobsByRouteDay(jobs);
    } catch (e) {
      setError(friendly(e));
      setEntries([]);
      setOpenDays([]);
      setRouteStarts({});
      setJobsByRouteDay({});
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, employeeId]);

  useEffect(() => { load(); }, [load]);

  const rangeTotal = useMemo(
    () => entries.reduce((sum, e) => sum + (workedHours(e.clock_in_at, e.clock_out_at) || 0), 0),
    [entries]
  );

  const exportCsv = () => {
    const header = ['Date', 'Employee', 'Start Time', 'Stop Time', 'Hours', 'Crossed TZ', 'Notes'];
    const lines = [header.map(csvEscape).join(',')];

    // Group by employee, with a subtotal row after each.
    const byEmp = new Map();
    for (const e of entries) {
      if (!byEmp.has(e.employee_id)) byEmp.set(e.employee_id, []);
      byEmp.get(e.employee_id).push(e);
    }
    for (const [empId, list] of byEmp) {
      let subtotal = 0;
      for (const e of list) {
        const h = workedHours(e.clock_in_at, e.clock_out_at);
        if (h != null) subtotal += h;
        lines.push([
          e.work_date,
          empName(empId),
          e.clock_in_at ? formatInOriginZone(e.clock_in_at, e.clock_in_zone) : '',
          e.clock_out_at ? formatInOriginZone(e.clock_out_at, e.clock_out_zone) : '',
          h != null ? h.toFixed(2) : (e.clock_in_at ? 'OPEN' : ''),
          e.crossed_timezones ? 'YES' : '',
          combinedNotes(e),
        ].map(csvEscape).join(','));
      }
      lines.push(['', `${empName(empId)} subtotal`, '', '', subtotal.toFixed(2), '', ''].map(csvEscape).join(','));
    }

    const csv = lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const who = employeeId ? empName(employeeId).replace(/\s+/g, '-').toLowerCase() : 'grant';
    a.download = `payroll_${who}_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const inputClass =
    'px-3 py-1.5 bg-gray-900 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600';

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-2">Time</h1>
      <p className="text-sm text-gray-400 mb-6">
        Actual worked hours from start/stop times. Hours are real elapsed time — a day that drove east reads an hour
        less than the clock difference, and that is correct.
      </p>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Employee</label>
          <div className="px-3 py-1.5 bg-gray-900 border border-gray-500 rounded-md text-white text-sm">
            Grant
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputClass} />
        </div>
        <button
          onClick={exportCsv}
          disabled={!entries.length}
          className="px-4 py-1.5 text-sm font-medium bg-green-700 text-white rounded-md hover:bg-green-600 disabled:opacity-40 transition-colors"
        >
          Export CSV
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-500/20 border border-red-500 rounded text-sm text-red-200">{error}</div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-gray-700">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key ? 'border-green-600 text-white' : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            {t.label}
            {t.key === 'open' && openDays.length > 0 && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300">{openDays.length}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : tab === 'entries' ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-700">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Employee</th>
                <th className="py-2 pr-3">Start Time</th>
                <th className="py-2 pr-3">Stop Time</th>
                <th className="py-2 pr-3 text-right">Hours</th>
                <th className="py-2 pr-3">Flags</th>
                <th className="py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const h = workedHours(e.clock_in_at, e.clock_out_at);
                return (
                  <tr key={e.id} className="border-b border-gray-700/50 text-gray-300">
                    <td className="py-2 pr-3 whitespace-nowrap">{e.work_date}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{empName(e.employee_id)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{e.clock_in_at ? formatInOriginZone(e.clock_in_at, e.clock_in_zone) : '—'}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{e.clock_out_at ? formatInOriginZone(e.clock_out_at, e.clock_out_zone) : (e.clock_in_at ? <span className="text-amber-300">open</span> : '—')}</td>
                    <td className="py-2 pr-3 text-right font-medium text-white">{h != null ? h.toFixed(2) : '—'}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {e.crossed_timezones && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">crossed</span>}
                    </td>
                    <td className="py-2 text-gray-400 max-w-xs truncate">{combinedNotes(e)}</td>
                  </tr>
                );
              })}
              {!entries.length && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-400">No entries in this range.</td></tr>
              )}
            </tbody>
            {entries.length > 0 && (
              <tfoot>
                <tr className="text-white font-semibold">
                  <td className="py-2 pr-3" colSpan={4}>Total</td>
                  <td className="py-2 pr-3 text-right">{rangeTotal.toFixed(2)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (
        <div className="space-y-2">
          {openDays.length === 0 && <p className="text-gray-400 text-sm">No open days — every entry has a stop time.</p>}
          {openDays.map((e) => (
            <div key={e.id} className="flex items-center justify-between px-3 py-2 bg-amber-400/5 border border-amber-400/30 rounded">
              <div className="text-sm text-gray-300">
                <span className="text-white font-medium">{empName(e.employee_id)}</span>
                <span className="text-gray-400"> · {e.work_date}</span>
                <span className="text-gray-400"> · start {formatInOriginZone(e.clock_in_at, e.clock_in_zone)}</span>
              </div>
              <span className="text-xs text-amber-300">awaiting stop time</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PayrollPage;
